import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { AutoComplete, Button, Space } from 'antd';
import * as actions from '@actions';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { FileTextOutlined, DeleteOutlined } from 'antd/icons';
import useAppointmentDetails from '@hooks/appointments/useAppointmentDetails';
import { useSessionFormState } from '@hooks/useSessionFormState';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import { useDebounce } from '@hooks/useDebounce';
import UnstyledButton from '@components/UI/UnstyledButton';
import { Accordion } from '@components/Clinicians/Accordion';
import { confirmationModal } from 'vl-common/src/components/Modals';
import useClinicalNotesMeta from '@hooks/useClinicalNotesMeta';

type SearchedFactsheet = {
  sheet_id: number;
  sheet_name: string;
  url: string;
  filename: string;
};

const FactsheetListItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: start;
  margin-top: 4px;
  gap: 0.5em;
`;

const FactsheetsFormItem = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const {
    appointment: { appointment_id, audit_appt_id, clinician_user_guid, appointment_completed, read_only }
  } = useSessionAppointment();
  const disabled = appointment_completed || read_only;
  const { detailsCurrentAppointment, refetch } = useAppointmentDetails(audit_appt_id || appointment_id);

  const [factsheetSearchText, setFactsheetSearchText] = useState('');
  const [selectedFactsheet, setSelectedFactsheet] = useState<SearchedFactsheet>();
  // Index of factsheet being removed. -1 if none
  const [indexOfDeletingFactsheet, setIndexOfDeletingFactsheet] = useState(-1);
  const [isCheckedManually, setIsCheckedManually] = useState(false);

  const isV2Appointment = detailsCurrentAppointment?.appt_version === 2;

  const [, setCurrentFactsheets] = useSessionFormState('factsheets', detailsCurrentAppointment?.factsheets);

  const { mutate: searchFactsheets, data: searchedFactsheets = [] } = useMutation({
    mutationFn: async (searchText: string) => {
      const res = await dispatch(actions.clinicianDOFactsheets(searchText, appointment_id));
      return (res as any).factsheets?.map((factsheet: SearchedFactsheet) => ({
        opt: factsheet,
        value: factsheet.sheet_name
      }));
    }
  });

  const { mutate: addFactsheet } = useMutation({
    mutationFn: () =>
      dispatch(
        actions.clinicianDOFactsheetSave({
          user_guid: clinician_user_guid,
          appt_id: appointment_id,
          factsheet_id: selectedFactsheet?.sheet_id
        })
      ),
    onSuccess: async () => {
      await refetch();
      setSelectedFactsheet(undefined);
      setFactsheetSearchText('');

      setCurrentFactsheets(detailsCurrentAppointment?.factsheets);
    }
  });

  const previewFactsheet = (url?: string) => {
    if (url) {
      window.open(url);
    }
  };

  const { mutate: removeFactsheet } = useMutation({
    mutationFn: ({ factsheet_id }: { factsheet_id: number; deletionIndex: number }) =>
      dispatch(
        actions.clinicianDOFactsheetSave({
          user_guid: clinician_user_guid,
          appt_id: appointment_id,
          factsheet_id,
          enabled: 0
        })
      ),
    onMutate: ({ deletionIndex }) => {
      setIndexOfDeletingFactsheet(deletionIndex);
    },
    onSuccess: async () => {
      await refetch();
      setIndexOfDeletingFactsheet(-1);
    }
  });

  const addedFactsheetsCount = Number(detailsCurrentAppointment?.factsheets.length);

  const removeAllFactsheets = () => {
    detailsCurrentAppointment?.factsheets.forEach((factsheet, index) => {
      removeFactsheet({
        factsheet_id: factsheet.factsheet_id,
        deletionIndex: index
      });
    });
  };

  const handleToggleAccordion = (checked: boolean) => {
    if (checked) {
      setIsCheckedManually(true);
      return;
    }

    if (!addedFactsheetsCount) {
      setIsCheckedManually(false);
      return;
    }

    confirmationModal({
      title: 'Are you sure?',
      content: `Your added factsheet${addedFactsheetsCount > 0 ? 's' : ''} will be deleted`,
      closable: true,
      onOk: () => {
        removeAllFactsheets();
        setIsCheckedManually(false);
      }
    });
  };

  const { getIsFieldRequired, getNoteLabel } = useClinicalNotesMeta();
  const isRequired = getIsFieldRequired('FACTSHEET');

  const isExpanded = !!addedFactsheetsCount || isCheckedManually;

  return (
    <Accordion
      isCollapsibleByCheckbox={isV2Appointment}
      disabled={disabled || read_only}
      label={getNoteLabel('FACTSHEET')}
      required={isRequired}
      {...(isV2Appointment && { expanded: isExpanded, onChange: handleToggleAccordion })}
    >
      <>
        <Space.Compact style={{ width: '100%' }}>
          <AutoComplete
            allowClear
            css={{ width: disabled ? '100%' : 'calc(100% - 148px)' }}
            options={factsheetSearchText ? searchedFactsheets : []}
            onSelect={(_, option) => setSelectedFactsheet(option.opt)}
            onSearch={useDebounce(searchFactsheets, 400)}
            onChange={(value) => setFactsheetSearchText(value)}
            onClear={() => setFactsheetSearchText('')}
            disabled={disabled}
            placeholder={t('Type to search')}
            value={factsheetSearchText}
            aria-label={t('Factsheet')}
          />
          {!disabled && (
            <>
              <Button
                type="default"
                css={{ width: '62px' }}
                disabled={!selectedFactsheet}
                onClick={() => addFactsheet()}
                data-testid="add-factsheet-button"
                aria-label={`${t('Add factsheet')} ${selectedFactsheet?.sheet_name}`}
              >
                {t('Add')}
              </Button>
              <Button
                type="default"
                css={{ width: '86px' }}
                disabled={!selectedFactsheet}
                data-testid="preview-factsheet-button"
                onClick={() => previewFactsheet(selectedFactsheet?.url)}
                aria-label={`${t('Preview factsheet')} ${selectedFactsheet?.sheet_name}`}
              >
                {t('Preview')}
              </Button>
            </>
          )}
        </Space.Compact>
        <ul style={{ padding: 0 }}>
          {detailsCurrentAppointment?.factsheets?.map((savedFactsheet, index) => (
            <FactsheetListItem key={savedFactsheet?.factsheet_id}>
              <UnstyledButton
                type="button"
                data-testid="view-factsheet-button"
                onClick={() => previewFactsheet(savedFactsheet.factsheet_url)}
                aria-label={`${t('View factsheet')} ${savedFactsheet.factsheet_name}`}
              >
                <FileTextOutlined aria-hidden /> {savedFactsheet.factsheet_name}
              </UnstyledButton>
              {!disabled && (
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined aria-hidden />}
                  data-testid="remove-factsheet-button"
                  disabled={indexOfDeletingFactsheet > -1}
                  loading={indexOfDeletingFactsheet === index}
                  onClick={() => removeFactsheet({ factsheet_id: savedFactsheet.factsheet_id, deletionIndex: index })}
                  aria-label={`${t('Remove factsheet')} ${savedFactsheet.factsheet_name}`}
                >
                  {t('Remove')}
                </Button>
              )}
            </FactsheetListItem>
          ))}
        </ul>
      </>
    </Accordion>
  );
};

export default FactsheetsFormItem;
